/**
 * Typed fetch wrapper around the API Gateway endpoint. Pulls the access token
 * from the auth store and attaches it as a Bearer header. Surfaces 4xx as
 * thrown ApiError objects so components can render meaningful messages.
 */
import { useAuthStore } from '@/stores/auth'
import type * as CompT from '@medlive-hris/shared-types'
import type { Address, AuditEntry, Cycle, Employee, JobDescription, PersonnelDoc, PersonnelDocDownload, PersonnelDocType, PersonnelDocUploadInit, Review, ReviewState, UserContext } from '@medlive-hris/shared-types'

export interface ApiErrorBody { error: string; message: string; details?: unknown }

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message); this.name = 'ApiError'
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const auth = useAuthStore()

  // Pre-flight session check. If the token's missing OR the OIDC
  // client already flagged it expired, block the send until the
  // store either silent-renews or kicks off a full redirect. This
  // is what stops the "nav link does nothing after the tab sat idle"
  // failure mode — before, request() threw 401 immediately and no
  // one caught it; now the store either brings the session back or
  // navigates the browser to the Cognito hosted UI.
  if (!auth.accessToken || auth.oidcUser?.expired) {
    const ok = await auth.ensureFreshSession()
    if (!ok) {
      // ensureFreshSession triggered a full redirect — the browser
      // is on its way to Cognito. Throw so the caller's promise
      // chain unwinds cleanly rather than continuing on with a
      // stale token.
      throw new ApiError(401, 'session_expired', 'Signing you back in…')
    }
  }

  const buildHeaders = (): Record<string, string> => {
    const h: Record<string, string> = {
      'Authorization': `Bearer ${auth.accessToken}`,
      'Content-Type': 'application/json',
    }
    // While HR is "viewing as" another user, attach impersonation headers
    // on EVERY request (GET + writes). The backend's getUserContext applies
    // env-aware policy:
    //   · prod → blocks non-GET methods (HR can read-as-them but can't
    //            write-as-them, so an HR mis-click can't corrupt review
    //            content under someone else's identity).
    //   · dev  → allows all methods so testers can drive end-to-end flows
    //            (submit a self-review AS Employee A, ack calibration AS
    //            Manager B, etc.) without re-logging-in each time. The
    //            audit log still attributes every write to the real HR
    //            caller via impersonatorId, so provenance stays clean.
    // See apps/api/src/lib/auth.ts for the authoritative rule.
    // HR powers only on HR screens - the API drops the HR role without
    // this header (apps/api/src/lib/auth.ts, web admin/hrContext.ts).
    if (!auth.isImpersonating && auth.hrContext && auth.isAdminUser) h['X-Hr-Context'] = 'on'
    if (auth.isImpersonating && auth.impersonationEmpId) {
      h['X-View-As'] = auth.impersonationEmpId
      h['X-View-As-Roles'] = (auth.impersonationRoles ?? []).join(',') || 'employee'
      h['X-View-As-HasReports'] = auth.impersonationHasReports ? 'true' : 'false'
    }
    return h
  }

  const send = () => fetch(`${import.meta.env.VITE_API_URL}${path}`, {
    method,
    headers: buildHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  let res = await send()

  // If the token expired between our pre-check and the server's
  // JWT authorizer, try one silent renewal + retry. Anything else
  // 401 (revoked token, changed scopes) falls through to the
  // normal error path.
  if (res.status === 401) {
    const ok = await auth.ensureFreshSession()
    if (!ok) throw new ApiError(401, 'session_expired', 'Signing you back in…')
    res = await send()
  }

  if (res.status === 204) return undefined as T
  const text = await res.text()
  const json = text ? JSON.parse(text) : {}
  if (!res.ok) {
    const e = json as ApiErrorBody
    throw new ApiError(res.status, e.error ?? 'unknown', e.message ?? res.statusText, e.details)
  }
  return json as T
}

// === Typed response shapes ===
export interface MeResponse {
  user: UserContext
  employee: Employee
  activeCycle: Cycle | null
  myReview: Partial<Review> | null
  myJd: JobDescription | null
  pastCycles: Cycle[]
  /** Delegation: who I'm covering for, and who covers me (see shared-types/delegation). */
  delegating?: CompT.DelegatingFor[]
  coveredBy?: CompT.CoveredBy[]
}
export interface JdDraftBody {
  mission: string
  responsibilities: Array<{ id?: string; text: string }>
  competencies: Array<{ id?: string; text: string }>
  note?: string | null
}
export interface MyReviewResponse {
  review: Partial<Review>
  jd: JobDescription | null
}
export interface TeamReviewEmployee {
  id: string
  displayName: string
  givenName: string
  familyName: string
  title: string
  department: string
  initials: string
  avatarColor: string
  photoUrl: string | null
  /**
   * Current manager-of-record from the live employee row (NOT the
   * snapshot on the Review). The My Team page uses this to group
   * subtree rows by current sub-manager. May be null/undefined for
   * top-level employees or for callers that didn't request the
   * subtree-mode payload.
   */
  managerId?: string | null
}
export interface TeamReviewItem {
  review: Partial<Review>
  employee: TeamReviewEmployee | null
}
export interface TeamReviewsResponse {
  cycleId: string
  // Slim cycle reference for the SPA — used by the add-to-cycle confirm
  // modal to show remaining time before due dates.
  cycle: {
    id: string
    name: string
    state: string
    selfDue: string
    managerDue: string
    closesAt: string
  }
  // True when there's no active cycle and the API fell back to the most
  // recently closed one. SPA renders a historical-context banner and
  // suppresses add/edit affordances on this surface.
  cycleIsHistorical?: boolean
  items: TeamReviewItem[]
  // Direct reports that exist in the manager's tree but don't yet have a
  // review in this cycle (late hires, post-cycle re-orgs, etc.). The team
  // page renders these dimmed with an "Add to cycle" affordance.
  unenrolledReports: TeamReviewEmployee[]
}
export interface ReviewWithJd {
  review: Partial<Review>
  jd: JobDescription | null
  employee: {
    id: string
    displayName: string
    givenName: string
    familyName: string
    title: string
    department: string
    initials: string
    avatarColor: string
    photoUrl: string | null
  } | null
}
export interface OrgRollupReview {
  reviewId: string
  employeeId: string
  managerId: string | null
  state: string
  lastEyAverage: number | null
  selfSubmittedAt: string | null
  managerSubmittedAt: string | null
  calibratedAt: string | null
  calibrationAckedAt: string | null
  releasedAt: string | null
  acknowledgedAt: string | null
  averages: { self: number | null; manager: number | null; final: number | null }
}
export interface OrgRollupEmployee {
  id: string
  managerId: string | null
  displayName: string
  givenName: string
  familyName: string
  email: string
  title: string
  department: string
  initials: string
  avatarColor: string
  photoUrl: string | null
  status: string
  // Derived from hrisGroups.executive on the backend. Drives the
  // "Include executive users" toggle on the Calibration page — exec
  // member rows are hidden by default during HR-run calibration so the
  // attending exec team doesn't see each other's pre-final scores.
  isExecutive: boolean
}
/**
 * One row of the cycle-picker payload (GET /reviews/cycles-for-me).
 * Lightweight: just metadata + viewer-scoped review counts. No review
 * data — the dashboard hits `orgRollup` once the picker selects a cycle.
 */
export interface MyCycleListing {
  id: string
  name: string
  type?: string
  state: string
  opensAt: string | null
  closesAt: string | null
  selfDue: string | null
  managerDue: string | null
  calibrationDue: string | null
  /** Reviews in this cycle whose subject is in scope for the viewer. */
  reviewCount: number
  /** Of those, how many are calibrated / released / acknowledged. */
  calibratedCount: number
}

/**
 * Per-cycle averages snapshot, returned in `OrgRollupResponse.historicalCycles`
 * when the caller asks for `includeHistory > 0`. Backs the multi-cycle
 * historical bar chart on Team Detail. Keys in `averages` are employeeIds
 * pre-scoped to whatever subtree filter the rollup request applied.
 */
export interface OrgRollupHistoricalCycle {
  cycleId: string
  cycleName: string
  type?: string
  opensAt: string | null
  closesAt: string | null
  averages: Record<string, number>
}

export interface OrgRollupResponse {
  cycle: Cycle
  // True when there's no active cycle and the API fell back to the most
  // recent closed one. SPA renders a "viewing past cycle" banner on the
  // org dashboard + cycle detail when this is set.
  cycleIsHistorical?: boolean
  employees: OrgRollupEmployee[]
  reviews: OrgRollupReview[]
  // Optional prior-cycle averages — returned only when the caller passes
  // `includeHistory > 0`. Ordered most-recent first. Same-type preferred
  // (an EY cycle's history is other EYs first, padded with non-EY only
  // if needed).
  historicalCycles?: OrgRollupHistoricalCycle[]
}

export type Rating = 1 | 1.5 | 2 | 2.5 | 3 | 3.5 | 4 | 4.5 | 5
export type Side = 'self' | 'manager'

// ── Debug view payload (HR-only) ─────────────────────────────────
// Mirrored from apps/api/src/handlers/reviews/get-debug.ts. Inline
// rather than in shared-types because it's an internal HR tool and
// the shape may iterate freely without a package bump.
export interface DebugKeyRow {
  storageKey: string
  shimmedKey: string
  matchType: 'ok' | 'orphan' | 'added' | 'added_orphan'
  section: 'responsibility' | 'competency' | 'added' | null
  matchedItemText: string | null
  value: number | string | null
}
export interface DebugItemRow {
  section: 'responsibility' | 'competency'
  index: number
  storageId: string
  shimmedId: string
  text: string
}
export interface DebugSectionedAvg {
  resp: number | null
  comp: number | null
  overall: number | null
}
export interface DebugPersonSlim {
  id: string
  displayName: string
  email: string
  title: string | null
  department: string | null
  status: string | null
  managerId: string | null
}
export interface ReviewDebugPayload {
  meta: { reviewId: string; cycleId: string; empId: string; fetchedAt: string }
  cycle: { id: string; name: string; type?: string; state: string; opensAt: string | null; closesAt: string | null } | null
  employee: DebugPersonSlim | null
  currentLiveManager: DebugPersonSlim | null
  reviewManagerOfRecord: DebugPersonSlim | null
  managerDrift: boolean
  reviewState: ReviewState
  reviewTimestamps: {
    createdAt: string | null
    updatedAt: string | null
    selfSubmittedAt: string | null
    managerSubmittedAt: string | null
    calibratedAt: string | null
    calibratedBy: string | null
    calibrationAckedAt: string | null
    calibrationAckedBy: string | null
    releasedAt: string | null
    releasedBy: string | null
    acknowledgedAt: string | null
  }
  activeJd: { id: string; version: number; effectiveStart: string; effectiveEnd: string | null; title: string; mission: string } | null
  jdOfRecord: { id: string; version: number; effectiveStart: string; effectiveEnd: string | null; title: string; mission: string } | null
  jdDrift: boolean
  collision: {
    detected: boolean
    respCount: number
    compCount: number
    total: number
    fingerprint: 'both_start_at_zero' | 'no_collision'
    explanation: string
  }
  items: { resp: DebugItemRow[]; comp: DebugItemRow[] }
  keyAnalysis: {
    selfRatings: DebugKeyRow[]
    managerRatings: DebugKeyRow[]
    finalRatings: DebugKeyRow[]
    selfNotes: DebugKeyRow[]
    managerNotes: DebugKeyRow[]
    calibrationNotes: DebugKeyRow[]
  }
  averages: {
    naive:      { self: number | null; manager: number | null; final: number | null }
    sectioned:  { self: DebugSectionedAvg; manager: DebugSectionedAvg; final: DebugSectionedAvg }
    clientView: { self: DebugSectionedAvg; manager: DebugSectionedAvg; final: DebugSectionedAvg }
    mismatchFlags: { self: boolean; manager: boolean; final: boolean }
  }
  addedItems: Array<{
    id: string
    section: 'responsibilities' | 'competencies'
    text: string
    addedBy: 'self' | 'manager'
    addedAt: string
    status: 'pending' | 'accepted_scored' | 'accepted_unscored' | 'declined' | null
    declineReason: string | null
    triagedBy: string | null
    triagedAt: string | null
    effectiveStatus: 'pending' | 'accepted_scored' | 'accepted_unscored' | 'declined'
  }>
  overrides: {
    jdMissionOverride: string | null
    jdResponsibilitiesOverride: unknown[] | null
    jdCompetenciesOverride: unknown[] | null
  }
  audit: Array<{
    id: string
    ts: string
    actorId: string
    actorName: string
    actorRole: string | null
    action: string
    field: string | null
    itemIdx: string | number | null
    note: string | null
    before: unknown
    after: unknown
  }>
  raw: { review: Review; activeJd: JobDescription | null; jdOfRecord: JobDescription | null }
}

// ── Cycle-wide health scan (HR-only) ─────────────────────────────
// Powers the row-level warning badges on Cycle Detail.
export type HealthFlagType = 'id_collision' | 'jd_drift' | 'orphan_keys' | 'avg_mismatch'
export type HealthSeverity = 'info' | 'warning' | 'error'
export interface HealthFlag {
  type: HealthFlagType
  severity: HealthSeverity
  detail: string
}
export interface CycleHealthItem {
  reviewId: string
  employeeId: string
  severity: HealthSeverity
  flags: HealthFlag[]
}
export interface CycleHealthPayload {
  cycleId: string
  checkedAt: string
  summary: { total: number; clean: number; warning: number; error: number }
  items: CycleHealthItem[]
}

// Shape mirrored from apps/api/src/lib/entra-sync.ts. Kept inline rather
// than importing from shared-types so the API can iterate on the
// internals without bumping the published types package.
export interface EntraSyncChange { id: string; displayName: string; email?: string }
export interface EntraSyncUpdate extends EntraSyncChange { changes: string[] }
export interface EntraSyncResult {
  startedAt: string
  finishedAt: string
  durationMs: number
  added: EntraSyncChange[]
  updated: EntraSyncUpdate[]
  deactivated: EntraSyncChange[]
  unchanged: number
  errors: Array<{ id?: string; displayName?: string; error: string }>
  source: 'http' | 'schedule'
  /** Deactivations the mass-termination guard refused to apply (see entra-sync.ts). */
  deactivationsHeld?: { reason: string; wouldDeactivate: EntraSyncChange[] }
}

export const api = {
  me: () => request<MeResponse>('GET', '/me'),
  updateMyProfile: (updates: Partial<{
    givenName: string
    familyName: string
    photoUrl: string | null
    address: Address | null
    phone: string
    tShirtSize: 'XS' | 'S' | 'M' | 'L' | 'XL' | '2XL' | '3XL'
    dietaryNotes: string
  }>) => request<Employee>('PATCH', '/me/profile', updates),
  listCycles: () => request<Cycle[]>('GET', '/cycles'),
  getMyReview: (cycleId?: string) =>
    request<MyReviewResponse>('GET', `/reviews/mine${cycleId ? `?cycleId=${cycleId}` : ''}`),
  getReview: (id: string) => request<ReviewWithJd>('GET', `/reviews/${id}`),
  /**
   * Team review payload. Defaults to direct reports only; pass
   * `subtree: true` to expand to the caller's whole reporting chain
   * (direct + indirect). The subtree variant also includes
   * `managerId` on each employee row so the SPA can group rows by
   * current sub-manager (mirrors the My Team's JDs page layout).
   */
  getTeamReviews: (cycleId?: string, opts: { subtree?: boolean } = {}) => {
    const params = new URLSearchParams()
    if (cycleId) params.set('cycleId', cycleId)
    if (opts.subtree) params.set('subtree', 'true')
    const qs = params.toString()
    return request<TeamReviewsResponse>('GET', `/reviews/team${qs ? `?${qs}` : ''}`)
  },
  // itemIdx is a numeric JD index OR the composite key `added:<id>` for items
  // beyond the JD. The backend handles both forms identically — keys land in
  // the same rating/note maps as strings.
  patchRating: (id: string, side: Side, itemIdx: number | string, rating: Rating | null) =>
    request<Review>('PATCH', `/reviews/${id}/ratings`, { side, itemIdx, rating }),
  patchNote: (id: string, side: Side, itemIdx: number | string, note: string | null) =>
    request<Review>('PATCH', `/reviews/${id}/notes`, { side, itemIdx, note }),
  patchSummary: (id: string, side: Side, summary: string | null) =>
    request<Review>('PATCH', `/reviews/${id}/summary`, { side, summary }),
  submitReview: (id: string, side: Side) =>
    request<Review>('POST', `/reviews/${id}/submit`, { side }),
  calibrateReview: (id: string, adjustments: Array<{ itemIdx: number | string; finalRating: Rating; note: string }>) =>
    request<Review>('POST', `/reviews/${id}/calibrate`, { adjustments }),
  // Manager-of-record acknowledges they've reviewed calibration changes
  // before doing the 1:1. Unlocks the release-after-1:1 transition.
  ackCalibration: (id: string) =>
    request<Review>('POST', `/reviews/${id}/ack-calibration`),
  releaseReview: (id: string) =>
    request<Review>('POST', `/reviews/${id}/release`),
  acknowledgeReview: (id: string) =>
    request<Review>('POST', `/reviews/${id}/acknowledge`),
  resetReview: (id: string, to: 'self' | 'manager', opts?: { wipeData?: boolean; reason?: string }) =>
    request<Review>('POST', `/reviews/${id}/reset`, { to, wipeData: opts?.wipeData ?? false, reason: opts?.reason }),
  /**
   * HR edit-mode only — remove an employee from a cycle by deleting the
   * Review row. Bypasses the closed-cycle guard. The reason is captured
   * in the audit log.
   */
  deleteReview: (id: string, reason?: string) =>
    request<{ deleted: true; reviewId: string }>('DELETE', `/reviews/${id}`, reason ? { reason } : undefined),
  /**
   * HR edit-mode only — force a review into a specific state, bypassing
   * the normal state-machine transition rules. Reason is required.
   * Used to fix imports stuck in a wrong state or to land a backfilled
   * review directly at `released`.
   */
  forceReviewState: (id: string, state: ReviewState, reason: string) =>
    request<Review>('PATCH', `/reviews/${id}/state`, { state, reason }),
  /**
   * HR-only edit-anything override for correcting historical reviews
   * that imported wrong. Send only the fields that should change —
   * undefined leaves a field alone, null clears it, value sets it.
   * The `reason` field is required and lands in the audit log.
   *
   * The three JD override fields (jdMissionOverride, etc.) supersede
   * the snapshotted JD content for THIS review only — they do not
   * mutate the underlying JobDescription row.
   */
  hrEditReview: (id: string, body: {
    reason: string
    jdMissionOverride?: string | null
    // Override arrays accept either bare strings or `{id?, text}` items,
    // matching the JD write surface. Ids ride through unchanged.
    jdResponsibilitiesOverride?: Array<string | { id?: string; text: string }> | null
    jdCompetenciesOverride?: Array<string | { id?: string; text: string }> | null
    selfRatings?: Record<string, Rating>
    selfNotes?: Record<string, string>
    selfSummary?: string | null
    managerRatings?: Record<string, Rating>
    managerNotes?: Record<string, string>
    managerSummary?: string | null
    finalRatings?: Record<string, Rating>
    calibrationNotes?: Record<string, string>
    managerId?: string | null
  }) => request<{
    review: Review
    changedFields: string[]
    auditId: string | null
  }>('PATCH', `/reviews/${id}/hr-edit`, body),
  /**
   * HR-only rich diagnostic payload for a single review. Everything
   * the debug page needs in one round-trip: raw review row + active
   * JD + JD-of-record, collision analysis, key-by-key rating/note
   * breakdown with storage vs shimmed key comparison, three
   * parallel average computations, added items, JD overrides, and
   * the audit trail for this review. Read-only.
   */
  getReviewDebug: (id: string) =>
    request<ReviewDebugPayload>('GET', `/reviews/${id}/debug`),
  /**
   * HR-only cycle-wide health scan. Returns per-review flag lists so
   * the Cycle Detail page can decorate rows with warning badges and
   * HR can spot at a glance which reviews need drilling into.
   */
  getCycleHealth: (cycleId: string) =>
    request<CycleHealthPayload>('GET', `/reviews/health?cycleId=${encodeURIComponent(cycleId)}`),
  /**
   * HR-only bulk-delete of orphan rating/note map entries. Sweeps
   * all six maps (self / manager / final ratings + self / manager /
   * calibration notes) in one call. The typed reason lands in the
   * audit log alongside a full before/after snapshot of the maps
   * that changed. See handlers/reviews/repair-orphans.ts.
   */
  repairReviewOrphans: (reviewId: string, reason: string) =>
    request<{
      review: Review
      deleted: Record<string, Array<{ key: string; value: unknown }>>
      totalDeleted: number
      auditId: string | null
    }>('POST', `/reviews/${reviewId}/repair-orphans`, { reason }),
  /**
   * Bulk nudge — send reminders to everyone stuck in a given review-state
   * bucket for a cycle. Buckets:
   *   · self_pending    → employees who owe their self-review
   *   · manager_pending → managers who owe a manager review
   *   · calibrated      → managers whose calibrated reviews still need
   *                        the 1:1 conversation + release
   *   · released        → employees who haven't acknowledged the
   *                        released review yet
   */
  nudgeReviews: (bucket: 'self_pending' | 'manager_pending' | 'calibrated' | 'released', cycleId: string) =>
    request<{ sent: number; skipped: number; errors: Array<{ name: string; reason: string }>; message?: string }>(
      'POST', '/reviews/nudge', { bucket, cycleId },
    ),
  /**
   * Per-row nudge. Same four sides as the bulk endpoint's derived
   * NudgeSide: 'self' / 'manager' / 'manager_release' / 'employee_ack'.
   */
  nudgeReview: (reviewId: string, side: 'self' | 'manager' | 'manager_release' | 'employee_ack') =>
    request<{ sent: number; to: string; recipientName: string; recipientId: string }>(
      'POST', `/reviews/${reviewId}/nudge`, { side },
    ),
  addReviewItem: (reviewId: string, section: 'responsibilities' | 'competencies', text: string) =>
    request<Review>('POST', `/reviews/${reviewId}/items`, { section, text }),
  declineReviewItem: (reviewId: string, itemId: string) =>
    request<Review>('DELETE', `/reviews/${reviewId}/items/${itemId}`),
  // Manager triages an employee-added item: accept and rate it normally,
  // accept without rating (kept visible, ignored in averages), or decline
  // with a reason. The decline reason is required and surfaces to the
  // employee on their released review.
  triageReviewItem: (reviewId: string, itemId: string, mode: 'accept_scored' | 'accept_unscored' | 'decline', reason?: string) =>
    request<Review>('POST', `/reviews/${reviewId}/items/${itemId}/triage`, { mode, reason }),
  // Enroll a single late report into an existing cycle. Idempotent — the
  // alreadyEnrolled flag tells the caller whether a new row was written.
  addEmployeeToCycle: (cycleId: string, empId: string) =>
    request<{ review: Review; alreadyEnrolled: boolean }>(
      'POST', `/cycles/${cycleId}/reviews/${empId}`,
    ),
  /**
   * Bulk-enroll the caller's full subtree (self + every direct +
   * indirect report) into a cycle. Idempotent — anyone already enrolled
   * is reported back in `alreadyEnrolled`. Kickoff emails fire for
   * each newly added member except the caller.
   */
  addTeamToCycle: (cycleId: string) =>
    request<{
      cycleId: string
      cycleName: string
      added: Array<{ empId: string; displayName: string }>
      alreadyEnrolled: Array<{ empId: string; displayName: string }>
      skipped: Array<{ empId: string; displayName: string; reason: string }>
      total: number
      emails: { sent: number; failed: number; skipped: number }
    }>('POST', `/cycles/${cycleId}/reviews/team`),
  // `includeInactive` is HR-only on the server. When set, the
  // response also carries terminated employees so HR-only surfaces
  // (JD admin, personnel file archive) can show separated people.
  listEmployees: (opts: { includeInactive?: boolean } = {}) =>
    request<Employee[]>('GET', `/employees${opts.includeInactive ? '?includeInactive=1' : ''}`),
  /**
   * HR-only: returns every employee with `hidden: true`. Powers the
   * Hidden Users management page. The standard `listEmployees()`
   * endpoint excludes hidden rows entirely; this is the only way to
   * see them so HR can unhide later.
   */
  listHiddenEmployees: () =>
    request<Employee[]>('GET', '/employees/hidden'),
  // HR-triggered Entra sync. Returns the same SyncResult shape the
  // nightly scheduled job writes to CloudWatch, so the UI summary modal
  // can render the same diff the schedule would have done overnight.
  syncFromEntra: (opts: { confirmMassDeactivation?: boolean } = {}) =>
    request<EntraSyncResult>('POST', `/employees/sync-from-entra${opts.confirmMassDeactivation ? '?confirmMassDeactivation=1' : ''}`),

  // ── Personnel docs (HR-only) ────────────────────────────────────
  initPersonnelDocUpload: (empId: string, body: {
    docType: PersonnelDocType
    displayName?: string | null
    filename: string
    mimeType: string
    bytes: number
    replacesDocId?: string | null
  }) => request<PersonnelDocUploadInit>('POST', `/employees/${empId}/personnel-docs`, body),
  finalizePersonnelDoc: (empId: string, docId: string) =>
    request<PersonnelDoc>('POST', `/personnel-docs/${empId}/${docId}/finalize`),
  listPersonnelDocsForEmployee: (empId: string, opts: { includeDeleted?: boolean } = {}) =>
    request<PersonnelDoc[]>('GET', `/employees/${empId}/personnel-docs${opts.includeDeleted ? '?includeDeleted=1' : ''}`),
  listAllPersonnelDocs: (opts: { docType?: PersonnelDocType; includeDeleted?: boolean } = {}) => {
    const qs: string[] = []
    if (opts.docType) qs.push(`docType=${encodeURIComponent(opts.docType)}`)
    if (opts.includeDeleted) qs.push('includeDeleted=1')
    return request<PersonnelDoc[]>('GET', `/personnel-docs${qs.length ? `?${qs.join('&')}` : ''}`)
  },
  downloadPersonnelDoc: (empId: string, docId: string) =>
    request<PersonnelDocDownload>('GET', `/personnel-docs/${empId}/${docId}/download`),
  deletePersonnelDoc: (empId: string, docId: string) =>
    request<{ deleted: true; docId: string }>('DELETE', `/personnel-docs/${empId}/${docId}`),
  updateEmployee: (id: string, updates: Partial<{
    displayName: string
    givenName: string
    familyName: string
    title: string
    department: string
    managerId: string | null
    reviewExempt: boolean
    /** YYYY-MM-DD. HR-only (the whole endpoint is HR-gated). */
    hireDate: string
    /**
     * System-wide visibility kill switch. When true, the employee is
     * excluded from every read path other users see — People directory,
     * org chart, manager team views, cycle enrollments, dashboards,
     * audit log. Used for accounts that exist in Entra (and could
     * otherwise pull through here) but shouldn't appear in HRIS at all
     * — board chair, super-admin service accounts, etc. The HR Hidden
     * Users page is the only surface where hidden rows still show.
     */
    hidden: boolean
    photoUrl: string | null
    address: Address | null
    phone: string
    personalEmail: string
    birthday: string
    tShirtSize: 'XS' | 'S' | 'M' | 'L' | 'XL' | '2XL' | '3XL'
    dietaryNotes: string
    emergencyContact: string
    hrisGroups: Partial<{ employee: boolean; manager: boolean; executive: boolean; hr: boolean; finance: boolean }>
    delegation: Pick<CompT.Delegation, 'all' | 'byArea' | 'from' | 'to' | 'note'> | null
  }>) => request<Employee>('PATCH', `/employees/${id}`, updates),
  getEmployeeGroups: (id: string) =>
    request<{ employee: boolean; manager: boolean; executive: boolean; hr: boolean; finance: boolean; financeConfigured?: boolean }>(
      'GET', `/employees/${id}/groups`,
    ),
  /**
   * Org-wide rollup. By default returns the scope appropriate for the
   * caller's role (HR → whole org, non-HR exec → their subtree).
   * Pass `scope: 'subtree'` to force subtree scoping for callers that
   * want a "your org" view even when the user also holds HR — the
   * executive dashboard uses this so an HR-with-exec lands on their
   * leadership chain, not the entire company.
   */
  /**
   * Lists cycles the viewer has data in (with subtree scoping when
   * `?scope=subtree`). Used by the Org Dashboard's cycle picker — only
   * cycles where the viewer's team has reviews show up, and a per-cycle
   * `reviewCount` powers the smart-default selection.
   */
  listMyCycles: (opts: { scope?: 'subtree' } = {}) => {
    const params = new URLSearchParams()
    if (opts.scope) params.set('scope', opts.scope)
    const qs = params.toString()
    return request<{ cycles: MyCycleListing[] }>('GET', `/reviews/cycles-for-me${qs ? `?${qs}` : ''}`)
  },

  orgRollup: (opts: { cycleId?: string; scope?: 'subtree'; includeHistory?: number } = {}) => {
    const params = new URLSearchParams()
    if (opts.cycleId) params.set('cycleId', opts.cycleId)
    if (opts.scope) params.set('scope', opts.scope)
    // includeHistory > 0 asks the rollup to include the N most-recent
    // prior closed cycles' per-employee averages — exec dashboard uses
    // this for the Team Detail multi-cycle bar chart. Server caps at 6.
    if (opts.includeHistory && opts.includeHistory > 0) {
      params.set('includeHistory', String(opts.includeHistory))
    }
    const qs = params.toString()
    return request<OrgRollupResponse>('GET', `/reviews/all${qs ? `?${qs}` : ''}`)
  },

  // ── HR / cycles ───────────────────────────────────────────────
  createCycle: (cycle: {
    id: string; name: string; type: 'H1' | 'EY'
    opensAt: string; closesAt: string
    selfDue: string; managerDue: string; calibrationDue: string
    oneOnOneReleaseDue: string; releaseDate: string
    appliesTo: 'all' | string[]
    /**
     * When `false`, suppresses the cycle-opened kickoff email blast at
     * create time — the cycle still goes live in `open` state, but HR
     * is on the hook to send a coordinated announcement later (via
     * nudges or an external comms channel). Omit or pass `true` for
     * the normal launch path.
     */
    notifyEmployees?: boolean
  }) => request<{
    cycle: Cycle
    reviewsCreated: number
    emailsSent: number
    emailsFailed: number
    emailsSkipped: number
    notificationsSuppressed: boolean
  }>('POST', '/cycles', cycle),
  patchCycle: (id: string, updates: Partial<Cycle>) =>
    request<Cycle>('PATCH', `/cycles/${id}`, updates),
  closeCycle: (id: string) =>
    request<Cycle>('POST', `/cycles/${id}/close`),
  /**
   * HR-only nuclear option: deletes the cycle row AND every review under
   * it. Requires `confirmName` to match the cycle's name exactly — the
   * server rejects (409) if they differ. Primarily used for cleaning up
   * test cycles in dev; available in prod too for HR error recovery.
   */
  deleteCycle: (id: string, confirmName: string, reason?: string) =>
    request<{ deleted: true; cycleId: string; cycleName: string; reviewsRemoved: number }>(
      'DELETE', `/cycles/${id}`, { confirmName, ...(reason ? { reason } : {}) },
    ),
  // Exec/HR — generate a team summary PDF for one manager's subtree.
  // Returns a presigned download URL. Auth + scoping is enforced server-
  // side: non-HR execs can only request managers in their own subtree.
  exportTeamPdf: (managerId: string, opts: { cycleId?: string } = {}) => {
    const params = new URLSearchParams()
    if (opts.cycleId) params.set('cycleId', opts.cycleId)
    const qs = params.toString()
    return request<{
      managerId: string
      cycleId: string
      key: string
      url: string
      subteamCount: number
      memberCount: number
      expiresIn: number
    }>('POST', `/export/team/${managerId}/pdf${qs ? `?${qs}` : ''}`)
  },

  // Exec/HR — send a personal note from the exec to the manager via SES.
  // Body content is exec-supplied; server prefills are done client-side
  // and shown in a dialog before submit. Returns the recipient details
  // so the SPA can render a confirmation toast naming who got the email.
  sendTeamNote: (body: { managerId: string; subject: string; body: string; cycleId?: string | null }) =>
    request<{
      sent: true
      to: string
      recipientName: string
      recipientId: string
      replyTo: string
      cycleId: string | null
    }>('POST', '/messages/team-note', body),

  /**
   * Export a cycle archive. Two modes:
   *   · scope='whole' (default, HR only) — every review in the cycle.
   *     Used by the HR cycle-detail page.
   *   · scope='subtree' — only the caller's direct + indirect reports,
   *     EXCLUDING the caller themselves. Used by the executive Org
   *     Dashboard "Export org pack" button. Applies even for
   *     HR-with-exec users, because the button context is "my team,"
   *     not "the org."
   */
  exportCycle: (id: string, opts: { scope?: 'whole' | 'subtree' } = {}) => {
    const q = opts.scope ? `?scope=${encodeURIComponent(opts.scope)}` : ''
    return request<{
      cycleId: string
      key: string
      url: string
      reviewCount: number
      // PDFs successfully built — may be lower than reviewCount if a
      // subset failed (e.g. missing JD). Per-row errors travel in `errors`.
      pdfsBuilt: number
      errors: Array<{ employeeId: string; error: string }>
      expiresIn: number
    }>(
      'POST', `/export/cycle/${id}/zip${q}`,
    )
  },

  // ── HR / JDs ──────────────────────────────────────────────────
  // listJds:
  //   · no args       → caller's own scope (own JDs for an employee, own +
  //                     direct reports' JDs for a manager, everything for HR).
  //   · { employeeId }→ single-employee history (caller must own or manage them).
  //   · { subtree:true} → managers only: expand scope to indirect reports too.
  listJds: (opts?: string | { employeeId?: string; subtree?: boolean }) => {
    const qs: string[] = []
    if (typeof opts === 'string') qs.push(`employeeId=${opts}`)
    else if (opts) {
      if (opts.employeeId) qs.push(`employeeId=${opts.employeeId}`)
      if (opts.subtree) qs.push('subtree=true')
    }
    return request<JobDescription[]>('GET', `/jds${qs.length ? `?${qs.join('&')}` : ''}`)
  },
  /**
   * JD write shape supports either legacy bare strings or the new
   * `{ id?, text }` per-item objects. Callers that already carry ids
   * from a fetched JD should send them back on save so the server
   * preserves item identity across the edit — that's what keeps
   * rating maps aligned when items are reordered or removed.
   * `id` is optional; missing ids get minted server-side.
   */
  createJd: (jd: {
    employeeId: string; title: string; mission: string
    responsibilities: Array<string | { id?: string; text: string }>
    competencies: Array<string | { id?: string; text: string }>
    effectiveStart: string
  }) => request<JobDescription>('POST', '/jds', jd),
  patchJd: (id: string, updates: Partial<{
    title: string; mission: string
    responsibilities: Array<string | { id?: string; text: string }>
    competencies: Array<string | { id?: string; text: string }>
  }>) => request<JobDescription>('PATCH', `/jds/${id}`, updates),

  // ── HR / audit ────────────────────────────────────────────────
  listAudit: (filters?: { cycleId?: string; reviewId?: string; since?: string; until?: string }) => {
    const q = new URLSearchParams()
    if (filters?.cycleId) q.set('cycleId', filters.cycleId)
    if (filters?.reviewId) q.set('reviewId', filters.reviewId)
    if (filters?.since) q.set('since', filters.since)
    if (filters?.until) q.set('until', filters.until)
    const qs = q.toString()
    return request<{ items: AuditEntry[] }>('GET', `/audit${qs ? `?${qs}` : ''}`)
  },

  // ══════════════════════════════════════════════════════════════
  // Hiring module
  // ══════════════════════════════════════════════════════════════

  /**
   * List every opening the caller can see + a per-opening applicant
   * funnel stats block. Uses a fan-out on the server so we don't
   * have to make N+1 requests from the client.
   */
  listHiringOpenings: () =>
    request<{ openings: HiringOpeningWithStats[] }>('GET', '/hiring/openings'),
  createHiringOpening: (body: HiringOpeningDraft) =>
    request<{ opening: HiringOpening }>('POST', '/hiring/openings', body),
  getHiringOpening: (id: string) =>
    request<{ opening: HiringOpening; applications: HiringApplication[] }>('GET', `/hiring/openings/${encodeURIComponent(id)}`),
  patchHiringOpening: (id: string, patch: HiringOpeningPatch) =>
    request<{ opening: HiringOpening }>('PATCH', `/hiring/openings/${encodeURIComponent(id)}`, patch),
  /**
   * Full application detail for the focus review workflow.
   * Includes a short-lived presigned S3 URL for the resume PDF.
   */
  listHiringApplicants: () =>
    request<{
      applicants: Array<{
        application: HiringApplication
        opening: { id: string; title: string; department: string }
      }>
    }>('GET', '/hiring/applicants'),
  getHiringApplication: (openingId: string, appId: string) =>
    request<{
      application: HiringApplication
      candidate: import('@medlive-hris/shared-types').Candidate | null
      events: import('@medlive-hris/shared-types').ApplicationEvent[]
      resumeUrl: string
      /** Resolved opening questions — labels-for-ids lookup. */
      questions: HiringQuestion[]
      /** Standard-field config the opening published on the careers form. */
      standardFieldsConfig: HiringStandardConfig
      otherApplications: Array<{ id: string; openingId: string; stage: HiringApplicationStage; submittedAt: string }>
    }>('GET', `/hiring/openings/${encodeURIComponent(openingId)}/applications/${encodeURIComponent(appId)}`),
  /**
   * HR/reviewer-triggered re-score of a single applicant. Enqueues
   * a fresh SQS message to the AI scoring worker + flips the row
   * back to `aiPending: true` immediately so the UI shows the
   * spinner without waiting for the worker.
   */
  rescoreHiringApplication: (openingId: string, appId: string) =>
    request<{ enqueued: true; applicationId: string }>(
      'POST',
      `/hiring/openings/${encodeURIComponent(openingId)}/applications/${encodeURIComponent(appId)}/rescore`,
    ),
  patchHiringApplication: (openingId: string, appId: string, patch: {
    managerScore?: number | null
    managerScoreNote?: string
    stage?: HiringApplicationStage
    rejectionReason?: string | null
    noteBody?: string
  }) =>
    request<{ application: HiringApplication }>(
      'PATCH',
      `/hiring/openings/${encodeURIComponent(openingId)}/applications/${encodeURIComponent(appId)}`,
      patch,
    ),

  // Email templates (HR-only)
  listHiringTemplates: () =>
    request<{ templates: HiringEmailTemplate[] }>('GET', '/hiring/templates'),
  createHiringTemplate: (body: HiringTemplateDraft) =>
    request<{ template: HiringEmailTemplate }>('POST', '/hiring/templates', body),
  updateHiringTemplate: (id: string, body: HiringTemplateDraft) =>
    request<{ template: HiringEmailTemplate }>('PATCH', `/hiring/templates/${encodeURIComponent(id)}`, body),
  deleteHiringTemplate: (id: string) =>
    request<{ deleted: true; id: string }>('DELETE', `/hiring/templates/${encodeURIComponent(id)}`),

  // Application question bank
  //
  // list is available to anyone with hiring access; the CRUD
  // mutations are HR-only (server enforces). The client mirrors
  // that so the OpeningEditor can populate its question-picker
  // without HR involvement.
  listHiringQuestions: () =>
    request<{ questions: HiringQuestion[] }>('GET', '/hiring/questions'),
  createHiringQuestion: (body: HiringQuestionDraft) =>
    request<{ question: HiringQuestion }>('POST', '/hiring/questions', body),
  updateHiringQuestion: (id: string, body: HiringQuestionDraft & { force?: boolean }) =>
    request<{ question: HiringQuestion }>('PATCH', `/hiring/questions/${encodeURIComponent(id)}`, body),
  deleteHiringQuestion: (id: string) =>
    request<{ deleted: true; id: string }>('DELETE', `/hiring/questions/${encodeURIComponent(id)}`),

  // ══════════════════════════════════════════════════════════════
  // Time & Attendance module
  // ══════════════════════════════════════════════════════════════
  //
  // The balance endpoint returns 204 for employees without an
  // assigned policy — the SPA hides the module in that case.

  taBalance: () =>
    request<{ enrolled: boolean; snapshot?: TaBalanceSnapshot; message?: string } | undefined>('GET', '/ta/balance'),
  taListMyRequests: () =>
    request<{ requests: TaRequest[] }>('GET', '/ta/requests/mine'),
  taListPending: () =>
    request<{ requests: TaRequestWithEmployee[] }>('GET', '/ta/requests/pending'),
  taSubmitRequest: (body: TaSubmission) =>
    request<{ request: TaRequest }>('POST', '/ta/requests', body),
  taApprove: (empId: string, id: string, body: { managerNote?: string | null }) =>
    request<{ request: TaRequest }>('POST', `/ta/requests/${encodeURIComponent(empId)}/${encodeURIComponent(id)}/approve`, body),
  taDeny: (empId: string, id: string, body: { managerNote?: string | null }) =>
    request<{ request: TaRequest }>('POST', `/ta/requests/${encodeURIComponent(empId)}/${encodeURIComponent(id)}/deny`, body),
  taCancel: (id: string, body: { reason?: string | null }) =>
    request<{ request: TaRequest; cancelled?: boolean; awaitingManager?: boolean }>('POST', `/ta/requests/${encodeURIComponent(id)}/cancel`, body),
  taCalendar: (scope: 'me' | 'team' | 'department' | 'company', fromDate: string, toDate: string) =>
    request<{ events: TaCalendarEvent[] }>('GET', `/ta/calendar?scope=${scope}&from=${fromDate}&to=${toDate}`),
  // Admin (HR-only)
  taListPolicies: () =>
    request<{ policies: (TaPolicy & { assignedCount: number })[] }>('GET', '/ta/policies'),
  taCreatePolicy: (body: TaPolicyDraft) =>
    request<{ policy: TaPolicy }>('POST', '/ta/policies', body),
  taUpdatePolicy: (id: string, body: TaPolicyDraft) =>
    request<{ policy: TaPolicy }>('PATCH', `/ta/policies/${encodeURIComponent(id)}`, body),
  taDeletePolicy: (id: string) =>
    request<{ deleted: true; id: string }>('DELETE', `/ta/policies/${encodeURIComponent(id)}`),
  taListAssignments: () =>
    request<{ rows: TaAssignmentRow[] }>('GET', '/ta/assignments'),
  taAssignPolicy: (empId: string, category: TaCategory, body: { policyId: string; effectiveFrom?: string }) =>
    request<{ assignment: TaAssignment }>('PUT', `/ta/assignments/${encodeURIComponent(empId)}/${encodeURIComponent(category)}`, body),
  taUnassignPolicy: (empId: string, category: TaCategory) =>
    request<{ deleted: true; empId: string; category: TaCategory }>('DELETE', `/ta/assignments/${encodeURIComponent(empId)}/${encodeURIComponent(category)}`),
  /**
   * Bulk-apply a policy across the whole company. `mode: 'all'`
   * overwrites everyone's assignment for that category; `mode:
   * 'unassigned'` only touches employees who don't have one yet.
   */
  taBulkAssign: (body: { category: TaCategory; policyId: string; mode: 'all' | 'unassigned' }) =>
    request<{ assigned: number; skipped: number; errors: Array<{ empId: string; reason: string }> }>('POST', '/ta/assignments/bulk', body),
  /** Pull hire dates from BambooHR — HR-only. */
  taImportBambooHireDates: () =>
    request<{
      hrisTotal: number
      matched: number
      updated: number
      unchanged: number
      notMatched: Array<{ empId: string; email: string; displayName: string }>
      errors: Array<{ email: string; reason: string }>
    }>('POST', '/ta/import/bamboo-hire-dates', {}),
  /** HR: pull personal (home) emails from BambooHR into the HR-only personalEmail field. */
  taImportBambooPersonalEmails: (dryRun = false) =>
    request<{
      dryRun: boolean
      hrisTotal: number
      matched: number
      updated: number
      unchanged: number
      bambooBlank: number
      changes: Array<{ empId: string; displayName: string; before: string | null; after: string }>
      notMatched: Array<{ empId: string; email: string; displayName: string }>
      errors: Array<{ email: string; reason: string }>
    }>('POST', '/ta/import/bamboo-personal-emails', { dryRun }),

  // ── JD change approvals ──
  /** My direct reports' JD status (approved version + open change). */
  jdTeam: () => request<import('@medlive-hris/shared-types').JdStatusResponse>('GET', '/jd-changes/team'),
  /** HR: org-wide JD status. */
  jdStatus: () => request<import('@medlive-hris/shared-types').JdStatusResponse>('GET', '/jd-changes/status'),
  /** Changes waiting on me (scope 'all' = HR: every pending change). */
  jdApprovals: (scope?: 'all') => request<{
    rows: Array<{
      employee: import('@medlive-hris/shared-types').JdPersonRef
      editor: import('@medlive-hris/shared-types').JdPersonRef | null
      approver: import('@medlive-hris/shared-types').JdPersonRef | null
      submittedAt: string | null
      updatedAt: string
    }>
    frozen: boolean
    frozenCycleName: string | null
  }>('GET', `/jd-changes/approvals${scope ? `?scope=${scope}` : ''}`),
  jdChangeGet: (empId: string) =>
    request<import('@medlive-hris/shared-types').JdChangeDetail>('GET', `/jd-changes/${encodeURIComponent(empId)}`),
  jdChangeSave: (empId: string, body: JdDraftBody) =>
    request<import('@medlive-hris/shared-types').JdChange>('PUT', `/jd-changes/${encodeURIComponent(empId)}`, body),
  /** Save (optional body) + submit. Returns 'published' for auto/HR routes. */
  jdChangeSubmit: (empId: string, body?: JdDraftBody) =>
    request<{ status: 'pending' | 'published'; route: string; approver?: import('@medlive-hris/shared-types').JdPersonRef | null }>(
      'POST', `/jd-changes/${encodeURIComponent(empId)}/submit`, body ?? {}),
  jdChangeWithdraw: (empId: string) =>
    request<{ status: 'withdrawn' }>('POST', `/jd-changes/${encodeURIComponent(empId)}/withdraw`, {}),
  jdChangeApprove: (empId: string) =>
    request<{ status: 'approved' }>('POST', `/jd-changes/${encodeURIComponent(empId)}/approve`, {}),
  jdChangeDeny: (empId: string, note: string) =>
    request<{ status: 'denied' }>('POST', `/jd-changes/${encodeURIComponent(empId)}/deny`, { note }),
  jdRemind: () => request<{ sent: number; approvers: number; managers: number }>('POST', '/jd-changes/remind', {}),

  /** HR: one employee's reviews across every cycle (Admin → Reviews → Employee Reviews). */
  reviewHistory: (empId: string) =>
    request<import('@medlive-hris/shared-types').ReviewHistoryResponse>('GET', `/review-history/${encodeURIComponent(empId)}`),

  // ── Required JD update cycles ──
  /** HR: every JD update cycle + counts, plus the open performance cycle (blocks launch). */
  jdReviews: () => request<{ reviews: import('@medlive-hris/shared-types').JdReviewSummary[]; activeCycle: { id: string; name: string } | null }>('GET', '/jd-reviews'),
  jdReviewGet: (id: string) => request<import('@medlive-hris/shared-types').JdReviewDetail>('GET', `/jd-reviews/${encodeURIComponent(id)}`),
  jdReviewCreate: (body: { name: string; scope: 'all' | 'people'; employeeIds?: string[]; dueDate: string; autoReminder?: boolean }) =>
    request<import('@medlive-hris/shared-types').JdReview>('POST', '/jd-reviews', body),
  jdReviewPatch: (id: string, body: Partial<{ name: string; scope: 'all' | 'people'; employeeIds: string[]; dueDate: string; autoReminder: boolean }>) =>
    request<import('@medlive-hris/shared-types').JdReview>('PATCH', `/jd-reviews/${encodeURIComponent(id)}`, body),
  jdReviewDelete: (id: string) => request<{ deleted: true }>('DELETE', `/jd-reviews/${encodeURIComponent(id)}`),
  jdReviewLaunch: (id: string) =>
    request<{ review: import('@medlive-hris/shared-types').JdReview; people: number; managersEmailed: number }>('POST', `/jd-reviews/${encodeURIComponent(id)}/launch`, {}),
  jdReviewAdd: (id: string, employeeIds: string[]) =>
    request<{ added: number; managersEmailed: number }>('POST', `/jd-reviews/${encodeURIComponent(id)}/add`, { employeeIds }),
  jdReviewRemind: (id: string) => request<{ managersEmailed: number }>('POST', `/jd-reviews/${encodeURIComponent(id)}/remind`, {}),
  /** Close. With open items, pass { completeRemaining: true, note } to close them all out with one reason. */
  jdReviewClose: (id: string, opts: { completeRemaining?: boolean; note?: string } = {}) =>
    request<import('@medlive-hris/shared-types').JdReview & { closedOut?: number }>('POST', `/jd-reviews/${encodeURIComponent(id)}/close`, opts),
  /** HR: complete one person's item on the manager's behalf (reason required). */
  jdReviewComplete: (id: string, empId: string, note: string) =>
    request<import('@medlive-hris/shared-types').JdReviewItemView>('POST', `/jd-reviews/${encodeURIComponent(id)}/items/${encodeURIComponent(empId)}/complete`, { note }),
  /** Manager: my open JD update cycle items. */
  jdReviewsMine: () => request<import('@medlive-hris/shared-types').MyJdReviewsResponse>('GET', '/jd-reviews/mine'),

  // ── Compensation module ─────────────────────────────────────────
  compMine: () => request<CompT.CompMineResponse>('GET', '/comp/mine'),
  compSettings: () => request<CompT.CompSettings>('GET', '/comp/settings'),
  compSettingsSave: (body: Pick<CompT.CompSettings, 'paylocityCompanyId' | 'payFrequency' | 'changeReason' | 'pctStep' | 'proportional'>) => request<CompT.CompSettings>('PUT', '/comp/settings', body),
  compData: () => request<CompT.CompDataResponse>('GET', '/comp/data'),
  compDataSave: (empId: string, body: { salary: number; mbo: number; ote: number; payType: CompT.PayType; paylocityId: string | null; effectiveDate?: string; note?: string }) =>
    request<CompT.CompRecord>('PUT', `/comp/data/${encodeURIComponent(empId)}`, body),
  compDataHistory: (empId: string) => request<{ rows: CompT.CompHistoryRow[] }>('GET', `/comp/data/${encodeURIComponent(empId)}/history`),
  compImport: (rows: CompT.CompImportRow[], apply: boolean, fileName?: string) => request<CompT.CompImportResult>('POST', '/comp/data/import', { rows, apply, fileName }),
  compCycles: () => request<{ cycles: CompT.CompCycleSummary[]; executives: CompT.CompPersonRef[]; ceo: CompT.CompPersonRef | null }>('GET', '/comp/cycles'),
  compCycleGet: (id: string) => request<CompT.CompCycleSummary>('GET', `/comp/cycles/${encodeURIComponent(id)}`),
  compCycleCreate: (body: { name: string; year: number; effectiveDate: string; includeExecTeam?: boolean }) =>
    request<CompT.CompCycle>('POST', '/comp/cycles', body),
  compWorksheet: (id: string) => request<CompT.CompWorksheet>('GET', `/comp/cycles/${encodeURIComponent(id)}/worksheet`),
  /** Finance: name/year/effectiveDate/defaultPct/budget/overrides (draft only). HR or Finance: execDue/ceoDue/autoReminder. */
  compCyclePatch: (id: string, body: Partial<{ name: string; year: number; effectiveDate: string; includeExecTeam: boolean; defaultPct: number | null; budget: number | null; overrides: Record<string, CompT.CompTeamOverride>; execDue: string | null; ceoDue: string | null; autoReminder: boolean }>) =>
    request<CompT.CompCycle>('PATCH', `/comp/cycles/${encodeURIComponent(id)}`, body),
  compCycleDelete: (id: string) => request<{ deleted: true }>('DELETE', `/comp/cycles/${encodeURIComponent(id)}`),
  compCycleLaunch: (id: string, body: { execDue: string; ceoDue: string; autoReminder?: boolean }) => request<CompT.CompCycleSummary & { executivesEmailed: number }>('POST', `/comp/cycles/${encodeURIComponent(id)}/launch`, body),
  compCycleRemind: (id: string) => request<{ executivesEmailed: number }>('POST', `/comp/cycles/${encodeURIComponent(id)}/remind`, {}),
  compCycleClose: (id: string) => request<CompT.CompCycle & { people: number; total: number }>('POST', `/comp/cycles/${encodeURIComponent(id)}/close`, {}),
  compCycleExport: (id: string, kind: 'paylocity' | 'summary', opts: { approvedOnly?: boolean; preview?: boolean } = {}) =>
    request<CompT.CompExportResponse>('GET', `/comp/cycles/${encodeURIComponent(id)}/export?kind=${kind}&approvedOnly=${opts.approvedOnly === false ? '0' : '1'}${opts.preview ? '&preview=1' : ''}`),
  compTeam: (cycleId: string, execId: string) => request<CompT.CompTeamView>('GET', `/comp/cycles/${encodeURIComponent(cycleId)}/teams/${encodeURIComponent(execId)}`),
  compTeamSave: (cycleId: string, execId: string, body: { allocations: Record<string, number>; reason?: string | null }) =>
    request<CompT.CompTeamView>('PUT', `/comp/cycles/${encodeURIComponent(cycleId)}/teams/${encodeURIComponent(execId)}`, body),
  compTeamSubmit: (cycleId: string, execId: string, reason?: string | null) =>
    request<CompT.CompTeamView>('POST', `/comp/cycles/${encodeURIComponent(cycleId)}/teams/${encodeURIComponent(execId)}/submit`, { reason }),
  compTeamApprove: (cycleId: string, execId: string, body: { comment?: string; comments?: Record<string, string> } = {}) =>
    request<CompT.CompTeamView>('POST', `/comp/cycles/${encodeURIComponent(cycleId)}/teams/${encodeURIComponent(execId)}/approve`, body),
  compTeamSendBack: (cycleId: string, execId: string, body: { comment: string; comments?: Record<string, string> }) =>
    request<CompT.CompTeamView>('POST', `/comp/cycles/${encodeURIComponent(cycleId)}/teams/${encodeURIComponent(execId)}/send-back`, body),
  compTeamReopen: (cycleId: string, execId: string, note: string) =>
    request<CompT.CompTeamView>('POST', `/comp/cycles/${encodeURIComponent(cycleId)}/teams/${encodeURIComponent(execId)}/reopen`, { note }),
  /** Manager: "no changes needed" (or 'todo' to undo it). */
  jdReviewRespond: (id: string, empId: string, response: 'no_change' | 'todo') =>
    request<import('@medlive-hris/shared-types').JdReviewItemView>('POST', `/jd-reviews/${encodeURIComponent(id)}/items/${encodeURIComponent(empId)}/respond`, { response }),

  // ── Org settings (Admin → Modules) ──
  /** Which modules are on for the org. Any signed-in user. */
  getOrgModules: () =>
    request<import('@medlive-hris/shared-types').OrgModulesResponse>('GET', '/org/modules'),
  /** HR-only. Partial — send just the module(s) being changed. */
  updateOrgModules: (modules: Partial<import('@medlive-hris/shared-types').OrgModuleSettings>) =>
    request<import('@medlive-hris/shared-types').OrgModulesResponse>('PUT', '/org/modules', { modules }),
}

// ── Hiring wire types ──────────────────────────────────────────
// Re-exported from shared-types under aliases so hiring pages
// don't have to reach into the shared-types package for every
// field — the client is the sole import point.
export type HiringOpening = import('@medlive-hris/shared-types').JobOpening
export type HiringOpeningState = import('@medlive-hris/shared-types').JobOpeningState
export type HiringApplication = import('@medlive-hris/shared-types').Application
export type HiringApplicationStage = import('@medlive-hris/shared-types').ApplicationStage
export interface HiringOpeningWithStats extends HiringOpening {
  stats: { total: number; toReview: number; interview: number; offer: number }
}
export type HiringOpeningDraft = Omit<HiringOpening,
  'id' | 'aiPromptVersion' | 'state' | 'createdBy' | 'createdAt' | 'updatedBy' | 'updatedAt' | 'approvedBy' | 'approvedAt'
> & { aiScoringPrompt?: string }
export type HiringOpeningPatch = Partial<Omit<HiringOpening,
  'id' | 'aiPromptVersion' | 'createdBy' | 'createdAt' | 'updatedBy' | 'updatedAt' | 'approvedBy' | 'approvedAt'
>>
export type HiringEmailTemplate = import('@medlive-hris/shared-types').EmailTemplate
export type HiringEmailTrigger = import('@medlive-hris/shared-types').EmailTrigger
export interface HiringTemplateDraft {
  name: string
  trigger: HiringEmailTrigger
  subject: string
  body: string
  isDefault?: boolean
}

// ── Application question bank wire types ───────────────────────
export type HiringQuestion         = import('@medlive-hris/shared-types').ApplicationQuestion
export type HiringQuestionType     = import('@medlive-hris/shared-types').QuestionType
export type HiringStandardField    = import('@medlive-hris/shared-types').StandardApplicationField
export type HiringFieldEnrollment  = import('@medlive-hris/shared-types').FieldEnrollment
export type HiringStandardConfig   = import('@medlive-hris/shared-types').StandardFieldConfig
export type HiringApplicationConfig = import('@medlive-hris/shared-types').ApplicationConfig
export type HiringOpeningQuestionRef = import('@medlive-hris/shared-types').OpeningQuestionRef
export type HiringApplicationAnswers = import('@medlive-hris/shared-types').ApplicationAnswers
export type HiringEeoAnswers       = import('@medlive-hris/shared-types').EeoAnswers

/** Draft payload for question-bank writes — excludes server fields. */
export interface HiringQuestionDraft {
  type: HiringQuestionType
  label: string
  helpText?: string | null
  required: boolean
  options?: Array<{ value: string; label: string }>
  disqualifyOn?: string | null
  archived?: boolean
}

// ── Time & Attendance wire types ──────────────────────────────
export type TaCategory       = import('@medlive-hris/shared-types').TimeOffCategory
export type TaRequest        = import('@medlive-hris/shared-types').TimeOffRequest
export type TaRequestState   = import('@medlive-hris/shared-types').TimeOffRequestState
export type TaRequestWithEmployee = import('@medlive-hris/shared-types').TimeOffRequestWithEmployee
export type TaBalanceSnapshot = import('@medlive-hris/shared-types').EmployeeBalanceSnapshot
export type TaCategoryBalance = import('@medlive-hris/shared-types').CategoryBalance
export type TaCalendarEvent  = import('@medlive-hris/shared-types').CalendarEvent
export type TaCalendarScope  = import('@medlive-hris/shared-types').CalendarScope
export type TaSubmission     = import('@medlive-hris/shared-types').TimeOffRequestSubmission
export type TaPolicy          = import('@medlive-hris/shared-types').TimeOffPolicy
export type TaAccrualSchedule = import('@medlive-hris/shared-types').AccrualSchedule
export type TaMilestone       = import('@medlive-hris/shared-types').AccrualMilestone
export type TaAssignment      = import('@medlive-hris/shared-types').PolicyAssignment

export interface TaPolicyDraft {
  name: string
  description?: string | null
  category: TaCategory
  isDefault?: boolean
  startingSchedule: TaAccrualSchedule
  milestones: TaMilestone[]
  carryoverMonth: number
  carryoverDay: number
  resetNegativeOnCarryover?: boolean
  carryoverExpiration?: { unit: 'days' | 'months' | 'years'; amount: number } | null
  firstAccrualMode: 'full' | 'prorated'
  accrualTiming: 'start' | 'end'
  archived?: boolean
}

export interface TaAssignmentRow {
  empId: string
  displayName: string
  department: string
  hireDate: string | null
  tenureYears: number | null
  ptoPolicyId: string | null
  ptoPolicyName: string | null
  sickPolicyId: string | null
  sickPolicyName: string | null
  personalPolicyId: string | null
  personalPolicyName: string | null
}
